import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InventoryIcon from "@mui/icons-material/Inventory";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";

import sheetsPreview from "@/assets/noAvailablepreview/sheets.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useToast from "@/contexts/toast-context";
import { formatBytes } from "@/helpers/file-helpers";
import useActivePlans from "@/hooks/use-active-plans";
import useImportProducts from "@/hooks/use-import-products";
import { ProductsMessages } from "@/messages/products/product.messages";
import productService from "@/services/product.service";

const productsSampleFileUrl = `${import.meta.env.VITE_API_URL}/examples/produtos.xlsx`;

interface ProductImportModalProps {
  onClose: () => void;
  open: boolean;
  messages: ProductsMessages;
}

export function ProductsImportModal({ open, onClose, messages }: ProductImportModalProps) {
  const { getRootProps, getInputProps, uploadedFile, removeFile } = useImportProducts({ messages });
  const { hasActivePlans, isLoading: isLoadingPlans } = useActivePlans();
  const canDownloadSampleFile = hasActivePlans && !isLoadingPlans;
  const handleClose = () => {
    onClose();
    removeFile();
  };
  const { showToast } = useToast();

  const handleDownloadProductsSampleFile = async () => {
    if (!canDownloadSampleFile) {
      return;
    }

    const link = document.createElement("a");

    link.href = productsSampleFileUrl;
    link.download = "produtos.xlsx";
    link.target = "_blank";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportProducts = async (file: File | null) => {
    if (!file) {
      alert("Please upload a file before importing.");
      return;
    }

    try {
      const success = await productService.importProductsFromFile(file);
      if (!success) {
        showToast({ text: messages.importProductsError, type: "error" });
        return;
      }
      showToast({ text: messages.importProductsSuccess, type: "success" });

      handleClose();
    } catch (error) {
      showToast({ text: messages.importProductsError, type: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{messages.importProductsTitle}</DialogTitle>
      <DialogContent dividers>
        <MDBox p={3}>
          <IconButton
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              top: theme.functions.pxToRem(12),
              right: theme.functions.pxToRem(12),
            })}
          >
            <CloseIcon />
          </IconButton>
          <MDBox p={2} sx={{ border: "2px dashed " }} {...getRootProps()}>
            <input {...getInputProps()} />
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              {uploadedFile ? (
                <>
                  <img src={sheetsPreview} alt="Text Preview" width={88} height={110} />

                  <MDTypography variant="body2" textAlign="center">
                    {uploadedFile.name} - ({formatBytes(uploadedFile.size)})
                  </MDTypography>
                </>
              ) : (
                <>
                  <FileUploadIcon sx={{ width: 25, height: 25 }} />
                  <MDTypography variant="body2" textAlign="center">
                    {messages.importProductsPlaceholder}
                  </MDTypography>
                </>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton
          variant="gradient"
          color="light"
          disabled={!canDownloadSampleFile}
          onClick={handleDownloadProductsSampleFile}
          startIcon={<InventoryIcon />}
        >
          {messages.downloadImportProductsSampleFile}
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          disabled={!uploadedFile}
          onClick={() => handleImportProducts(uploadedFile)}
        >
          {messages.importProductsButton}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
